import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import LocalShippingOutlinedIcon from "@mui/icons-material/LocalShippingOutlined";
import ReceiptLongOutlinedIcon from "@mui/icons-material/ReceiptLongOutlined";
import { Box, Link, Stack, Typography } from "@mui/material";

import { CommonDialog } from "@/components/common/CommonDialog";
import { PrimaryButton } from "@/components/common";

import { CheckoutProductsAccordion } from "./CheckoutProductsAccordion";
import { CheckoutReviewRow } from "./CheckoutReviewRow";
import { marketplaceTypography as mt } from "../constants/marketplaceTypography";
import type {
  MarketplaceAddressFormValues,
  MarketplaceCheckoutDraft,
} from "../types";
import {
  formatAddressSummary,
  formatPriceBrl,
  isCheckoutAddressComplete,
} from "../utils/addressHelpers";
import { shouldUseEmbeddedMarketplacePayment } from "../utils/embeddedPayment";

interface CheckoutReviewDialogProps {
  open: boolean;
  draft: MarketplaceCheckoutDraft | null;
  loading?: boolean;
  onClose: () => void;
  onEditProducts: () => void;
  onEditShipping: () => void;
  onEditBilling: () => void;
  onConfirm: () => void;
}

function areAddressesEqual(
  a: MarketplaceAddressFormValues,
  b: MarketplaceAddressFormValues,
): boolean {
  return formatAddressSummary(a) === formatAddressSummary(b);
}

function getBlockingMessage(
  shippingComplete: boolean,
  billingComplete: boolean,
): string | null {
  if (!shippingComplete) return "Informe o endereço de entrega para continuar.";
  if (!billingComplete) return "Informe o endereço de cobrança para continuar.";
  return null;
}

/** Resumo editável antes de criar o pedido e abrir pagamento no dispensador. */
export function CheckoutReviewDialog({
  open,
  draft,
  loading = false,
  onClose,
  onEditProducts,
  onEditShipping,
  onEditBilling,
  onConfirm,
}: CheckoutReviewDialogProps) {
  if (!draft) return null;

  const { store, cartItems, addresses } = draft;
  const subtotal = cartItems.reduce(
    (sum, item) => sum + item.price * item.quantity,
    0,
  );

  const shippingComplete = addresses
    ? isCheckoutAddressComplete(addresses.shipping)
    : false;
  const billingSameAsShipping =
    addresses && areAddressesEqual(addresses.shipping, addresses.billing);
  const billingComplete = addresses
    ? billingSameAsShipping
      ? shippingComplete
      : isCheckoutAddressComplete(addresses.billing)
    : false;

  const canConfirm = shippingComplete && billingComplete;
  const blockingMessage = getBlockingMessage(shippingComplete, billingComplete);
  const usesEmbeddedPayment = shouldUseEmbeddedMarketplacePayment();

  const shippingValue = shippingComplete && addresses
    ? formatAddressSummary(addresses.shipping)
    : "Onde receber?";

  const billingValue =
    !addresses || !billingComplete
      ? "Endereço da fatura"
      : billingSameAsShipping
        ? "Mesmo endereço da entrega"
        : formatAddressSummary(addresses.billing);

  const handleClose = () => {
    if (loading) return;
    onClose();
  };

  const footer = (
    <Stack spacing={1.25} width="100%">
      <Stack direction="row" justifyContent="space-between" alignItems="center">
        <Typography sx={{ ...mt.body, fontWeight: 700 }}>Total estimado</Typography>
        <Typography sx={{ ...mt.price, color: "primary800.main", fontSize: "1.25rem" }}>
          {formatPriceBrl(subtotal)}
        </Typography>
      </Stack>
      <PrimaryButton
        width="100%"
        height="48px"
        loading={loading}
        disabled={!canConfirm}
        onClick={onConfirm}
      >
        Confirmar e ir para pagamento
      </PrimaryButton>
      {!canConfirm && blockingMessage ? (
        <Typography sx={{ ...mt.bodySecondary, color: "text.secondary", textAlign: "center" }}>
          {blockingMessage}
        </Typography>
      ) : null}
      <Stack direction="row" spacing={0.75} alignItems="flex-start" justifyContent="center">
        <InfoOutlinedIcon sx={{ fontSize: 16, color: "text.secondary", mt: 0.2 }} />
        <Typography sx={{ ...mt.bodySecondary, color: "text.secondary", textAlign: "center" }}>
          {usesEmbeddedPayment
            ? "O pagamento abre na página do dispensador. Use o X para voltar ao Fluxmed."
            : "No dispensador parceiro você escolhe PIX ou cartão."}
        </Typography>
      </Stack>
      <Link
        component="button"
        type="button"
        variant="body2"
        onClick={handleClose}
        disabled={loading}
        sx={{
          alignSelf: "center",
          color: "text.secondary",
          textDecoration: "none",
          "&:hover": { textDecoration: "underline" },
        }}
      >
        Voltar
      </Link>
    </Stack>
  );

  return (
    <CommonDialog
      isOpen={open}
      handleClose={handleClose}
      maxWidth="lg"
      sheetTitle="Revise seu pedido"
      sheetSubtitle={store.name}
      footer={footer}
      sheetSize="large"
    >
      <Stack
        spacing={1.5}
        sx={{
          px: { xs: 0, md: 0.5 },
          maxHeight: { xs: "55vh", md: "60vh" },
          overflowY: "auto",
          WebkitOverflowScrolling: "touch",
        }}
      >
        <CheckoutProductsAccordion items={cartItems} onEdit={onEditProducts} />

        <Box sx={{ pt: 0.5 }}>
          <CheckoutReviewRow
            icon={<LocalShippingOutlinedIcon fontSize="small" />}
            title="Entrega"
            value={shippingValue}
            complete={shippingComplete}
            placeholder={!shippingComplete}
            onAction={onEditShipping}
          />
          <CheckoutReviewRow
            icon={<ReceiptLongOutlinedIcon fontSize="small" />}
            title="Cobrança"
            value={billingValue}
            complete={billingComplete}
            placeholder={!billingComplete}
            onAction={onEditBilling}
          />
        </Box>
      </Stack>
    </CommonDialog>
  );
}
